'use client'

import { useEffect, useMemo, useRef, useState } from "react";

import { EventoPublicado, TipoEntrada } from "@/types";

import { EventoContent } from "./tabs/EventoContent";
import { PaymentWizard } from "./PaymentWizard";
import { RightCardComprar } from "./RightCardComprar";
import { SpotifySection } from "./tabs/SocialEmbeds";

import { MapaListItem } from "@/lib/services/mapas";

import { EventoProvider } from "../contexts/eventos";
import { useEvento } from "@/store/evento/evento";
import {
    useComprarButton,
    useBeforeUnloadRelease,
    usePrefetchOccupiedSeats,
    usePrefetchReservedStock
} from "../hooks";
import EventoBanner from "./banner/EventoBanner";

type Props = {
    evento: EventoPublicado;
    mapa: MapaListItem | null;
    tipos: TipoEntrada[];
}

export default function EventoSection({ evento, mapa, tipos }: Props) {
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [showFixedButton, setShowFixedButton] = useState(false);
    const comprarCardRef = useRef<HTMLDivElement>(null);

    const { set: setActiveTab, state: activeTab } = useEvento.use.tabs();
    const { state: entradas } = useEvento.use.entradas();
    const clearAll = useEvento.use.clearAll();

    const { message, canContinue } = useComprarButton(mapa);

    const funcionIds = useMemo(() => evento.funciones.map((f) => f.id), [evento.funciones]);

    usePrefetchOccupiedSeats(mapa ? funcionIds : []);
    usePrefetchReservedStock(!mapa ? funcionIds : []);

    useBeforeUnloadRelease();

    useEffect(() => {
        clearAll();
    }, [clearAll])

    // Detect when purchase card leaves viewport
    useEffect(() => {
        const el = comprarCardRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowFixedButton(!entry.isIntersecting);
            },
            { threshold: 0 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [activeTab, isPurchasing]);

    const handleComprarButton = () => {
        if (activeTab !== 'funciones') {
            setActiveTab('funciones')
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        if (entradas.length === 0) return;

        setIsPurchasing(true)
    }

    return (
        <EventoProvider evento={evento} mapa={mapa} tipos={tipos}>
            <div className="mb-4">

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 mt-2">
                    <h1 className="text-4xl lg:text-5xl font-display text-white shadow-sm min-w-0">
                        {evento.nombre}
                    </h1>

                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
                {activeTab !== 'funciones' && (
                    <div className="lg:col-span-4 order-first lg:order-last">
                        <div className="sticky top-4 space-y-6">
                            <div ref={comprarCardRef}>
                                {!isPurchasing && (
                                    <RightCardComprar
                                        evento={evento}
                                        onComprar={handleComprarButton}
                                        disabled={!canContinue && activeTab === 'funciones'}
                                    />
                                )}
                            </div>
                            {evento.artista && <SpotifySection artista={evento.artista} />}
                        </div>
                    </div>
                )}

                <div className={`${activeTab === 'funciones' ? 'lg:col-span-12' : 'lg:col-span-8'} order-last lg:order-first`}>
                    {activeTab !== 'funciones' && <EventoBanner evento={evento} />}
                    <EventoContent onComprar={handleComprarButton} isComprarDisabled={!canContinue} />
                </div>
            </div>

            {isPurchasing && <PaymentWizard onClose={() => setIsPurchasing(false)} />}

            {/* Fixed mobile buy button - appears when purchase card is out of view */}
            {showFixedButton && activeTab !== 'funciones' && !isPurchasing && (
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#161616] via-[#161616]/95 to-transparent lg:hidden z-50">
                    <button
                        onClick={handleComprarButton}
                        className="w-full bg-brand-red hover:bg-brand-red-dark text-white font-bold py-4 rounded-lg text-lg uppercase tracking-wide transition-colors shadow-lg"
                    >
                        Comprar
                    </button>
                </div>
            )}
        </EventoProvider>
    )
}
